import React, { useState, useEffect, useMemo } from "react";
import { getFlowStatus, formatTimeRemaining } from './warmupFlowUtils';
import ResearchInfoTabs from './EmailAiModes/ResearchInfoTabs';
import useSentRecipients from './EmailAiModes/useSentRecipients';
import NotesActivityFields, { hasNoteContent } from './NotesActivityFields';
import "./FlowSelectModal.css";

const EMPTY_NOTE_DRAFT = { activity: 'call', outcome: null, sentiment: null, text: '', excludeFromAi: false };

const FLOW_END_CONFIG = {
  completed: { icon: null, text: 'FIN', color: '#94a3b8', label: 'Flow Completed' },
  converted: { icon: 'fa fa-trophy', color: '#f59e0b', label: 'Converted' },
  disqualified: { icon: 'fa fa-ban', color: '#ef4444', label: 'Disqualified' },
  not_interested: { icon: 'fa fa-times-circle', color: '#f97316', label: 'Not Interested' },
  wrong_contact: { icon: 'fa fa-exclamation-triangle', color: '#6b7280', label: 'Wrong Contact' },
  nurture: { icon: 'fa fa-clock', color: '#3b82f6', label: 'Nurture' },
};

const getStepIcon = (step) => {
  const t = step?.type || step?.nodeType || '';
  if (t === 'email_sent' || t.includes('email')) return 'fa fa-paper-plane';
  if (t.includes('instagram')) return 'fab fa-instagram';
  if (t.includes('linkedin')) return 'fab fa-linkedin';
  if (t.includes('whatsapp')) return 'fab fa-whatsapp';
  if (t.includes('phone') || t.includes('call')) return 'fa fa-phone-alt';
  if (t === 'delay' || step?.nodeType === 'delay') return 'fa fa-hourglass-half';
  if (step?.nodeType === 'auto_check_reply') return 'fa fa-hourglass-half';
  if (t === 'condition' || step?.nodeType === 'condition') return 'fa fa-code-branch';
  if (t === 'end' || step?.nodeType === 'end') return 'fa fa-flag-checkered';
  if (t === 'log_notes' || step?.nodeType === 'log_notes') return 'fa fa-clipboard-list';
  return 'fa fa-bolt';
};

const getStepThemeClass = (step) => {
  const t = step?.type || step?.nodeType || '';
  if (t === 'email_sent' || t.includes('email')) return 'theme-email';
  if (t.includes('instagram')) return 'theme-instagram';
  if (t.includes('linkedin')) return 'theme-linkedin';
  if (t.includes('whatsapp')) return 'theme-whatsapp';
  if (t.includes('phone') || t.includes('call')) return 'theme-phone';
  if (step?.nodeType === 'auto_check_reply') return 'theme-wait';
  return 'theme-default';
};

const CONDITION_LABELS = {
  is_favorite: 'Favorite?', email_opened: 'Opened?', email_clicked: 'Clicked?',
  replied: 'Replied?', shown_interest: 'Interested?', qualified: 'Qualified?',
  meeting_booked: 'Meeting?', deal_closed: 'Deal?', responded_positively: 'Positive?',
  decision_maker: 'Decision Maker?', has_budget: 'Has Budget?', good_fit: 'Good Fit?',
  ready_to_buy: 'Ready to Buy?', needs_follow_up: 'Follow Up?', meeting_went_well: 'Meeting Went Well?',
  has_phone: 'Has Phone?', has_email: 'Has Email?', has_company: 'Has Company?',
  score_above: 'Score Above?',
};

const STEP_LABELS = {
  email_sent: 'Send Email', instagram_dm: 'Send Instagram DM', instagram_follow: 'Follow on Instagram',
  linkedin_message: 'Send LinkedIn Message', linkedin_connect: 'LinkedIn Connect',
  whatsapp: 'Send WhatsApp', delay: 'Wait', condition: 'Decision', end: 'End',
  log_notes: 'Log Notes',
};

const getStepLabel = (step, idx) => {
  if (step?.nodeType === 'condition' || step?.type === 'condition') {
    if (step.customQuestion) return step.customQuestion;
    if (step.condition && CONDITION_LABELS[step.condition]) return CONDITION_LABELS[step.condition];
  }
  if (step?.label) return step.label;
  const t = step?.type || step?.nodeType || '';
  return STEP_LABELS[t] || (t ? t.replace(/_/g, ' ') : `Step ${idx + 1}`);
};

// Verb shown on the "complete task" button, tuned per touchpoint type.
const ACTION_VERBS = {
  email_sent: 'Mark as Sent', newsletter: 'Mark as Sent',
  linkedin_connect: 'Mark as Connected', linkedin_message: 'Mark as Sent',
  instagram_dm: 'Mark as Sent', instagram_follow: 'Mark as Followed',
  whatsapp: 'Mark as Sent', phone_call: 'Mark as Called', sales_meeting: 'Mark as Done',
  lead_magnet: 'Mark as Sent',
};

const getActionVerb = (step) => {
  const t = step?.type || step?.nodeType || '';
  return ACTION_VERBS[t] || 'Mark as Done';
};

const findNextStepFromEdges = (flow, currentStep, handle = null) => {
  if (!currentStep?.id || !flow?.visualData?.edges) return null;
  const outgoing = flow.visualData.edges.filter(e => e.source === currentStep.id);
  if (outgoing.length === 0) return null;
  let edge = outgoing[0];
  if (handle) {
    const match = outgoing.find(e => e.sourceHandle === handle);
    if (match) edge = match;
  }
  return flow.steps?.find(s => s.id === edge.target) || null;
};

// Mirrors LeadSwipe's doesPathLeadToEnd: walk a decision path through
// non-interactive nodes (tags / 0-day delays) to see if it terminates in an
// end node — so we can warn before a decision disqualifies / ends the flow.
const doesPathLeadToEnd = (flow, fromStep, handle) => {
  let step = findNextStepFromEdges(flow, fromStep, handle);
  const visited = new Set();
  let depth = 0;
  while (step && depth < 15) {
    if (visited.has(step.id)) break;
    visited.add(step.id);
    if (step.nodeType === 'end' || step.type === 'end') {
      const endType = step.endType || 'completed';
      return { isEnd: true, endType, isDisqualified: ['disqualified', 'not_interested', 'wrong_contact'].includes(endType) };
    }
    const isNonInteractive = step.nodeType === 'tag' || (step.nodeType === 'delay' && (parseInt(step.delayDays) || 0) === 0);
    if (!isNonInteractive) break;
    step = findNextStepFromEdges(flow, step);
    depth++;
  }
  return { isEnd: false, endType: null, isDisqualified: false };
};

const formatDurationSinceStart = (startedAt) => {
  if (!startedAt) return null;
  const diff = Date.now() - startedAt;
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  if (days === 0) return 'today';
  if (days === 1) return '1 day ago';
  return `${days} days ago`;
};

const DAY_MS = 24 * 60 * 60 * 1000;

// Compact "time in status" label — days/hours/minutes, tuned for a live ticker.
const formatElapsedShort = (ms) => {
  if (ms == null || ms < 0) return null;
  const totalMinutes = Math.floor(ms / 60000);
  if (totalMinutes < 1) return 'just now';
  const days = Math.floor(totalMinutes / 1440);
  const hours = Math.floor((totalMinutes % 1440) / 60);
  const minutes = totalMinutes % 60;
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
};

// When did the contact enter the CURRENT step? The most recent completedAt in
// the step history marks that moment; fall back to the flow start otherwise.
const getStepEnteredAt = (flowState) => {
  if (!flowState) return null;
  const hist = flowState.stepHistory || {};
  let latest = 0;
  Object.values(hist).forEach(h => { if (h?.completedAt && h.completedAt > latest) latest = h.completedAt; });
  return latest || flowState.startedAt || null;
};

// Unlock date label, e.g. "Fri, Jul 25, 14:30".
const formatUnlockDate = (ts) => {
  if (!ts) return null;
  try {
    return new Date(ts).toLocaleString([], { weekday: 'short', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch (_) { return new Date(ts).toLocaleDateString(); }
};

const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

// ── LinkedIn dashboard helpers ──
const parseDecisionMakers = (raw) => {
  if (!raw) return [];
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    return Array.isArray(parsed) ? parsed.filter(dm => dm && (dm.name || dm.role || dm.email)) : [];
  } catch (_) { return []; }
};

const liProfileHref = (val) => {
  if (!val) return null;
  const v = String(val).trim();
  if (!v) return null;
  if (v.startsWith('http')) return v;
  return `https://www.linkedin.com/in/${v.replace(/^\/+|\/+$/g, '')}`;
};
const liCompanySearch = (name) => `https://www.linkedin.com/search/results/companies/?keywords=${encodeURIComponent(name || '')}`;
const liPeopleSearch = (q) => `https://www.linkedin.com/search/results/people/?keywords=${encodeURIComponent((q || '').trim())}`;
const isLinkedInStepType = (step) => (step?.type || step?.nodeType || '').toLowerCase().includes('linkedin');

// Which channel a manual touchpoint uses — drives the interaction record.
const getStepChannel = (step) => {
  const t = (step?.type || step?.nodeType || '').toLowerCase();
  if (t.includes('linkedin')) return 'linkedin';
  if (t.includes('instagram')) return 'instagram';
  if (t.includes('whatsapp')) return 'whatsapp';
  if (t.includes('phone') || t.includes('call')) return 'phone';
  if (t.includes('email') || t.includes('mail')) return 'email';
  return 'other';
};
// Manual person-to-person touchpoints where we must know WHO was contacted.
// Email is excluded here because sent mails are already tracked per-address
// via world_sent (see useSentRecipients).
const needsInteractionPick = (step) => ['linkedin', 'instagram', 'whatsapp', 'phone'].includes(getStepChannel(step));

const CHANNEL_META = {
  linkedin: { icon: 'fab fa-linkedin-in', label: 'LinkedIn' },
  instagram: { icon: 'fab fa-instagram', label: 'Instagram' },
  whatsapp: { icon: 'fab fa-whatsapp', label: 'WhatsApp' },
  phone: { icon: 'fa fa-phone-alt', label: 'Call' },
  email: { icon: 'fa fa-envelope', label: 'Email' },
  other: { icon: 'fa fa-bolt', label: 'Touch' },
};

const dmKey = (dm) => (dm?.email || dm?.name || '').trim().toLowerCase();

// Build an absolute href for a social/website value that may be stored as a
// bare handle or partial URL.
const buildSocialHref = (val, base) => {
  if (!val) return null;
  const v = String(val).trim();
  if (!v) return null;
  if (v.startsWith('http')) return v;
  return `${base}${v.replace(/^\/+/, '')}`;
};

// The research pipeline appends [IG Research]/[SEO] blocks to lead_description;
// strip them so the sidebar shows only the human-readable business description.
const cleanDescription = (raw) => {
  let t = String(raw || '').trim();
  if (!t) return '';
  t = t.replace(/\n?\[IG Research\][\s\S]*$/i, '').trim();
  t = t.replace(/\n?\[SEO\][\s\S]*$/i, '').trim();
  return t;
};

const FlowSelectModal = ({ contact, userId, warmupFlows, onClose, onStartFlow, onStopFlow, onReactivate, onCompleteStep, completing }) => {
  const [confirmDialog, setConfirmDialog] = useState(null);
  // Which button is mid-flight ('yes' | 'no' | 'action') — for the spinner.
  const [pending, setPending] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [showPaths, setShowPaths] = useState(false);
  const [showSidebar, setShowSidebar] = useState(true);
  // Re-adding a contact to a flow they were already in → confirmation dialog.
  const [startConfirm, setStartConfirm] = useState(null);
  // People the user says they contacted at the current step (multi-select).
  const [pickedKeys, setPickedKeys] = useState([]);
  // Profile link per picked existing person: { [key]: url }.
  const [pickedLinks, setPickedLinks] = useState({});
  // Manually added people (not on file): [{ id, name, role, linkedin }].
  const [customPeople, setCustomPeople] = useState([]);
  const [showCustomDm, setShowCustomDm] = useState(false);
  const [customName, setCustomName] = useState('');
  const [customRole, setCustomRole] = useState('');
  const [customLinkedin, setCustomLinkedin] = useState('');
  // Live clock — drives the "time in status" ticker and the delay countdown.
  const [now, setNow] = useState(() => Date.now());
  // Composer state for the flow "Log Notes" step (replica of the Notes composer).
  const [noteDraft, setNoteDraft] = useState(EMPTY_NOTE_DRAFT);

  useEffect(() => { if (!completing) setPending(null); }, [completing]);

  // Lock background scroll while the modal is open so the lead list behind
  // it can't scroll along (scroll-chaining) on trackpads.
  const isOpen = !!contact;
  useEffect(() => {
    if (!isOpen) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prevOverflow; };
  }, [isOpen]);

  // Tick every second while a flow is active so the status timer stays live.
  const hasActiveFlow = !!(contact?.warmupFlow && contact.warmupFlow.currentStepIndex >= 0);
  useEffect(() => {
    if (!hasActiveFlow) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [hasActiveFlow, contact?.id]);

  // Decision makers + already-emailed addresses (reuse the send pipeline's
  // source of truth so mails sent from the composer show up here too).
  const dmList = useMemo(() => parseDecisionMakers(contact?.lead_decision_makers), [contact?.lead_decision_makers]);
  const recipientEmails = useMemo(() => {
    const list = [];
    if (contact?.lead_email) list.push(contact.lead_email);
    dmList.forEach(d => { if (d?.email) list.push(d.email); });
    return list;
  }, [contact?.lead_email, dmList]);
  const { isContacted } = useSentRecipients(userId, contact?.id, recipientEmails);

  const currentStepIndex = contact?.warmupFlow?.currentStepIndex;
  // On step/contact change, reset the picker — but if we already reached out to
  // someone on this same channel earlier in the flow, pre-select that person so
  // later touchpoints are a one-tap confirm with their profile link ready.
  useEffect(() => {
    let picked = null;
    const fs = getFlowStatus(contact, warmupFlows, 'warmup');
    if (fs) {
      let step = fs.currentStep;
      if (step?.nodeType === 'delay' && fs.isReady) {
        const next = findNextStepFromEdges(fs.flow, step);
        if (next) step = next;
      }
      if (step && needsInteractionPick(step)) {
        const ch = getStepChannel(step);
        const prior = Array.isArray(contact?.dmInteractions) ? contact.dmInteractions : [];
        const sameChannel = prior.filter(it => (it.channel || '') === ch);
        if (sameChannel.length) {
          const last = sameChannel.reduce((a, b) => ((b.at || 0) > (a.at || 0) ? b : a));
          const key = (last.email || last.name || '').trim().toLowerCase();
          // The person is always rendered as a selectable row (dmList entry or a
          // fallback built from the interaction), so a matching key is enough.
          const dm = dmList.find(d => dmKey(d) === key);
          if (key) picked = { key, linkedinUrl: last.linkedinUrl || dm?.linkedinUrl || dm?.linkedin || '' };
        }
      }
    }
    setPickedKeys(picked ? [picked.key] : []);
    setPickedLinks(picked ? { [picked.key]: picked.linkedinUrl || '' } : {});
    setCustomPeople([]);
    setShowCustomDm(false);
    setCustomName('');
    setCustomRole('');
    setCustomLinkedin('');
    setNoteDraft(EMPTY_NOTE_DRAFT);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contact?.id, currentStepIndex]);

  if (!contact) return null;

  const flowStatus = getFlowStatus(contact, warmupFlows, 'warmup');
  const contactName = contact.business_name || contact.lead_name || contact.name || contact.lead_email?.split('@')[0] || 'Contact';

  // Prior runs of a given flow, read from the logged history (reason: stopped /
  // completed / replaced). Lets us warn before re-adding to the same flow.
  const flowHistory = Array.isArray(contact.warmupFlowHistory) ? contact.warmupFlowHistory : [];
  const REASON_LABELS = { stopped: 'stopped', completed: 'completed', replaced: 'replaced' };
  const getPriorRun = (flowId) => {
    const runs = flowHistory.filter(h => h.flowId === flowId);
    if (!runs.length) return null;
    return { count: runs.length, last: runs[runs.length - 1] };
  };
  // Past flow runs, newest first, for the idle-screen overview.
  const pastFlows = [...flowHistory].reverse();
  const handlePickFlow = (flow) => {
    const prior = getPriorRun(flow.id);
    if (prior) {
      const idx = prior.last.lastStepIndex;
      const step = flow.steps?.[idx];
      setStartConfirm({
        flowId: flow.id,
        flowName: flow.name,
        count: prior.count,
        reason: REASON_LABELS[prior.last.reason] || prior.last.reason || 'ended',
        stepLabel: step ? getStepLabel(step, idx) : null,
        stepNumber: typeof idx === 'number' ? idx + 1 : null,
        endedAt: prior.last.endedAt || null,
      });
    } else {
      onStartFlow(contact.id, flow.id);
    }
  };
  const contactEmail = contact.lead_email || '';
  const avatarUrl = contact.profile_image?.url || null;

  const Avatar = ({ size }) => (
    <div className={`fx-avatar fx-avatar--${size} ${avatarUrl ? 'fx-avatar--img' : ''}`}>
      {avatarUrl ? <img src={avatarUrl} alt="" /> : getInitials(contactName)}
    </div>
  );

  return (
    <div className="fx-overlay" onClick={onClose}>
      <div className={`fx-card ${flowStatus ? (showSidebar ? 'fx-card--wide' : 'fx-card--slim') : 'fx-card--idle'}`} onClick={(e) => e.stopPropagation()}>

        {flowStatus ? (() => {
          const { flow, flowState, currentStep, isReady, timeRemaining, stepIndex } = flowStatus;

          // Skip past ready delay nodes to show the actual actionable step
          let displayStep = currentStep;
          let displayReady = isReady;
          let displayTimeRemaining = timeRemaining;
          if (currentStep?.nodeType === 'delay' && isReady) {
            const actionStep = findNextStepFromEdges(flow, currentStep);
            if (actionStep) {
              displayStep = actionStep;
              displayReady = true;
              displayTimeRemaining = 0;
            }
          }

          const isCurrentCondition = displayStep?.nodeType === 'condition' || displayStep?.type === 'condition';

          let yesStep = null;
          let noStep = null;

          if (isCurrentCondition) {
            yesStep = findNextStepFromEdges(flow, displayStep, 'yes');
            noStep = findNextStepFromEdges(flow, displayStep, 'no');
          }

          const startedAt = flowState?.startedAt;

          // ── Live status timing ──
          const stepEnteredAt = getStepEnteredAt(flowState);
          const timeInStep = stepEnteredAt ? Math.max(0, now - stepEnteredAt) : null;
          // Escalate the ticker's tone the longer a contact sits on one step.
          const stepAgeLevel = timeInStep == null ? 'fresh'
            : timeInStep > 7 * DAY_MS ? 'stale'
            : timeInStep > 2 * DAY_MS ? 'aging'
            : 'fresh';
          const InStepTimer = () => (timeInStep == null ? null : (
            <div className={`fx-instep fx-instep--${stepAgeLevel}`} title={stepEnteredAt ? `Entered ${new Date(stepEnteredAt).toLocaleString()}` : ''}>
              <span className="fx-instep-pulse"></span>
              <i className="fa fa-hourglass-half"></i>
              <span className="fx-instep-label">In this step</span>
              <span className="fx-instep-time">{formatElapsedShort(timeInStep)}</span>
            </div>
          ));

          // Delay progress: how far through the wait window we are.
          const unlockAt = flowState?.nextStepAvailableAt || null;
          const delayTotal = (unlockAt && stepEnteredAt) ? Math.max(0, unlockAt - stepEnteredAt) : 0;
          const delayElapsed = (unlockAt && stepEnteredAt) ? Math.max(0, now - stepEnteredAt) : 0;
          const delayProgress = delayTotal > 0 ? Math.min(1, delayElapsed / delayTotal) : 0;

          // "Auto Check Reply" waits up to `timeoutDays` for a reply before the
          // backend auto-decides yes/no — surface the same countdown UI as a
          // regular delay so it's clear how long the wait window still runs.
          const isAutoCheckReply = displayStep?.nodeType === 'auto_check_reply';
          const autoCheckTimeoutMs = isAutoCheckReply ? (parseInt(displayStep.timeoutDays) || 3) * DAY_MS : 0;
          const autoCheckDeadline = (isAutoCheckReply && stepEnteredAt) ? stepEnteredAt + autoCheckTimeoutMs : null;
          const autoCheckRemaining = autoCheckDeadline != null ? Math.max(0, autoCheckDeadline - now) : null;
          const autoCheckProgress = (autoCheckDeadline && autoCheckTimeoutMs > 0)
            ? Math.min(1, Math.max(0, (now - stepEnteredAt) / autoCheckTimeoutMs)) : 0;

          const displayStepIndex = flow.steps.findIndex(s => s.id === displayStep?.id);
          const isEndStep = displayStep?.nodeType === 'end' || displayStep?.type === 'end';
          const isAutoStep = displayStep?.nodeType === 'auto_send_email' || displayStep?.nodeType === 'auto_check_reply';
          const isLogNotes = displayStep?.nodeType === 'log_notes';
          const isNonActionable = ['tag', 'delay', 'trigger'].includes(displayStep?.nodeType);
          const canAct = !!onCompleteStep;
          const busy = !!completing;

          // ── Decision-maker interaction tracking ──
          const stepChannel = getStepChannel(displayStep);
          // Contact has no reachable LinkedIn target → the channel is skipped.
          const linkedinInactive = contact.lead_linkedin_inactive === true;
          const mustPickDm = needsInteractionPick(displayStep) && displayReady
            && !(linkedinInactive && stepChannel === 'linkedin');
          const priorInteractions = Array.isArray(contact.dmInteractions) ? contact.dmInteractions : [];
          // Per-person rollup of everything we've done so far (LinkedIn/social +
          // emails sent via the composer), so the user sees who's already engaged.
          const engagementByKey = {};
          const touchPerson = (key, name, patch) => {
            if (!key) return;
            if (!engagementByKey[key]) engagementByKey[key] = { name: name || 'Unknown', channels: {}, custom: false };
            if (name && engagementByKey[key].name === 'Unknown') engagementByKey[key].name = name;
            Object.assign(engagementByKey[key], patch);
          };
          priorInteractions.forEach(it => {
            const key = (it.email || it.name || '').trim().toLowerCase();
            touchPerson(key, it.name, {
              custom: engagementByKey[key]?.custom || !!it.custom,
              linkedinUrl: it.linkedinUrl || engagementByKey[key]?.linkedinUrl || null,
            });
            if (key) engagementByKey[key].channels[it.channel || 'other'] = it.at || true;
          });
          // Overlay email evidence + LinkedIn profile from the DM list onto
          // matching people so every chip can link out to a profile.
          dmList.forEach(dm => {
            const key = dmKey(dm);
            const dmLi = dm.linkedinUrl || dm.linkedin || null;
            if (engagementByKey[key] && dmLi && !engagementByKey[key].linkedinUrl) {
              engagementByKey[key].linkedinUrl = dmLi;
            }
            if (dm?.email && isContacted(dm.email)) {
              touchPerson(key, dm.name, { linkedinUrl: engagementByKey[key]?.linkedinUrl || dmLi });
              if (key) engagementByKey[key].channels.email = engagementByKey[key].channels.email || true;
            }
          });
          const engagementList = Object.entries(engagementByKey).map(([key, v]) => ({ key, ...v }));

          // Selectable people = decision makers on file + anyone we already
          // interacted with earlier in the flow (e.g. a person added manually at
          // step 1 who isn't in the DM list). This lets later touchpoints
          // pre-select and one-tap confirm the same contact.
          const pickList = (() => {
            const list = dmList.map(d => ({ ...d }));
            const seen = new Set(list.map(d => dmKey(d)));
            priorInteractions.forEach(it => {
              const key = (it.email || it.name || '').trim().toLowerCase();
              if (!key || seen.has(key)) return;
              seen.add(key);
              list.push({
                name: it.name,
                role: it.role || null,
                email: it.email || null,
                linkedin: it.linkedinUrl || null,
                fromInteraction: true,
              });
            });
            return list;
          })();

          // For LinkedIn steps we ask the user to confirm the profile link of
          // whoever they connected with, so every tracked contact is reachable.
          const needsProfileLink = stepChannel === 'linkedin';

          // Everyone the user marked as contacted at this step (existing picks +
          // manually added people). Multi-select: not either/or.
          const selectedExisting = pickedKeys.map(k => {
            const dm = pickList.find(d => dmKey(d) === k);
            if (!dm) return null;
            return {
              key: k,
              name: dm.name,
              role: dm.role || dm.position || null,
              email: dm.email || null,
              linkedinUrl: (pickedLinks[k] || dm.linkedinUrl || dm.linkedin || ''),
              custom: !!dm.fromInteraction,
            };
          }).filter(Boolean);
          const selectedCustom = customPeople.map(c => ({
            key: c.id, name: c.name, role: c.role || null, email: null, linkedinUrl: c.linkedin || '', custom: true,
          }));
          const selectedAll = [...selectedExisting, ...selectedCustom];

          // Valid once at least one person is chosen — and on LinkedIn every
          // chosen person needs a profile link.
          const hasValidPick = selectedAll.length > 0
            && selectedAll.every(p => p.name && p.name.trim())
            && (!needsProfileLink || selectedAll.every(p => p.linkedinUrl && p.linkedinUrl.trim()));
          const interactionReady = !mustPickDm || hasValidPick;

          // Can the in-progress manual entry be added? (name always; link on LI)
          const canAddCustom = !!customName.trim() && (!needsProfileLink || !!customLinkedin.trim());
          const addCustomPerson = () => {
            if (!canAddCustom) return;
            setCustomPeople(prev => [...prev, {
              id: `c_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
              name: customName.trim(), role: customRole.trim() || null, linkedin: customLinkedin.trim() || null,
            }]);
            setCustomName(''); setCustomRole(''); setCustomLinkedin(''); setShowCustomDm(false);
          };
          const removeCustomPerson = (id) => setCustomPeople(prev => prev.filter(c => c.id !== id));

          const togglePerson = (key, dm) => {
            if (pickedKeys.includes(key)) {
              setPickedKeys(pickedKeys.filter(k => k !== key));
              setPickedLinks(prev => { const n = { ...prev }; delete n[key]; return n; });
            } else {
              setPickedKeys([...pickedKeys, key]);
              setPickedLinks(prev => ({ ...prev, [key]: dm.linkedinUrl || dm.linkedin || prev[key] || '' }));
            }
          };

          const buildInteractions = () => {
            if (!needsInteractionPick(displayStep) || !selectedAll.length) return null;
            const base = Date.now();
            return selectedAll.map((p, i) => ({
              id: `${base}_${i}_${Math.random().toString(36).slice(2, 7)}`,
              name: (p.name || '').trim(),
              role: p.role || null,
              email: p.email || null,
              linkedinUrl: p.linkedinUrl || null,
              channel: stepChannel,
              stepIndex: displayStepIndex,
              stepLabel: getStepLabel(displayStep, stepIndex),
              at: base,
              custom: !!p.custom,
            }));
          };

          // Selectable people rows (shared by the LinkedIn dashboard and the
          // standalone picker for other manual channels). Multi-select: tick as
          // many as were contacted, then add extra people manually.
          const renderDmRows = () => (
            <>
              {pickList.length ? pickList.map((dm, i) => {
                const key = dmKey(dm);
                const hasLi = !!(dm.linkedinUrl || dm.linkedin);
                const href = liProfileHref(dm.linkedinUrl || dm.linkedin) || liPeopleSearch(`${dm.name || ''} ${contactName}`);
                const eng = engagementByKey[key];
                const contactedHere = !!(eng && eng.channels && eng.channels[stepChannel]);
                const emailed = !!(eng && eng.channels && eng.channels.email);
                const isPicked = pickedKeys.includes(key);
                return (
                  <React.Fragment key={i}>
                  <div
                    className={`fx-dm ${mustPickDm ? 'is-selectable' : ''} ${isPicked ? 'is-picked' : ''}`}
                    onClick={mustPickDm ? () => togglePerson(key, dm) : undefined}
                  >
                    {mustPickDm && (
                      <span className="fx-dm-radio">
                        <i className={isPicked ? 'fa fa-check-square' : 'far fa-square'}></i>
                      </span>
                    )}
                    <div className="fx-dm-avatar">{getInitials(dm.name)}</div>
                    <div className="fx-dm-info">
                      <span className="fx-dm-name">{dm.name || 'Unknown'}</span>
                      {(dm.role || dm.position) && <span className="fx-dm-role">{dm.role || dm.position}</span>}
                      {(contactedHere || emailed) && (
                        <span className="fx-dm-tags">
                          {contactedHere && <span className="fx-dm-tag li"><i className={CHANNEL_META[stepChannel]?.icon || 'fa fa-check'}></i> Contacted</span>}
                          {emailed && <span className="fx-dm-tag mail"><i className="fa fa-envelope"></i> Emailed</span>}
                        </span>
                      )}
                    </div>
                    <div className="fx-dm-links" onClick={(e) => e.stopPropagation()}>
                      <a className="fx-dm-li" href={href} target="_blank" rel="noopener noreferrer" title={hasLi ? 'Open LinkedIn profile' : 'Search on LinkedIn'}>
                        <i className="fab fa-linkedin-in"></i>
                      </a>
                      {dm.email && <a className="fx-dm-mail" href={`mailto:${dm.email}`} title={dm.email}><i className="fa fa-envelope"></i></a>}
                    </div>
                  </div>
                  {mustPickDm && isPicked && needsProfileLink && !hasLi && (
                    <div className="fx-dm-linkfield" onClick={(e) => e.stopPropagation()}>
                      <span className="fx-dm-linkfield-label">
                        <i className="fab fa-linkedin-in"></i> Link to {(dm.name || '').trim().split(/\s+/)[0] || 'their'}'s profile
                      </span>
                      <label className={`fx-field ${!(pickedLinks[key] || '').trim() ? 'is-required' : ''}`}>
                        <i className="fa fa-link"></i>
                        <input
                          placeholder="https://linkedin.com/in/…"
                          value={pickedLinks[key] || ''}
                          onChange={(e) => setPickedLinks(prev => ({ ...prev, [key]: e.target.value }))}
                        />
                      </label>
                    </div>
                  )}
                  </React.Fragment>
                );
              }) : (
                !mustPickDm && <div className="fx-dm-empty"><i className="fa fa-user-slash"></i> No decision makers yet</div>
              )}

              {/* Manually added people (chips with their profile link) */}
              {mustPickDm && customPeople.map(c => (
                <div className="fx-dm is-picked is-custom" key={c.id}>
                  <span className="fx-dm-radio"><i className="fa fa-check-square"></i></span>
                  <div className="fx-dm-avatar">{getInitials(c.name)}</div>
                  <div className="fx-dm-info">
                    <span className="fx-dm-name">{c.name}</span>
                    {c.role && <span className="fx-dm-role">{c.role}</span>}
                  </div>
                  <div className="fx-dm-links" onClick={(e) => e.stopPropagation()}>
                    {c.linkedin && <a className="fx-dm-li" href={liProfileHref(c.linkedin)} target="_blank" rel="noopener noreferrer" title="Open LinkedIn profile"><i className="fab fa-linkedin-in"></i></a>}
                    <button type="button" className="fx-dm-remove" title="Remove" onClick={() => removeCustomPerson(c.id)}><i className="fa fa-times"></i></button>
                  </div>
                </div>
              ))}

              {mustPickDm && (() => {
                // Nobody on file/added yet → open the manual-entry form straight
                // away. Otherwise "add another person" stays a collapsed toggle.
                const autoCustom = pickList.length === 0 && customPeople.length === 0;
                const customOpen = showCustomDm || autoCustom;
                return (
                  <div className="fx-addwrap">
                    {!autoCustom && (
                      <button type="button" className={`fx-addtrigger ${showCustomDm ? 'is-open' : ''}`} onClick={() => setShowCustomDm(v => !v)}>
                        <span className="fx-addtrigger-ic"><i className={showCustomDm ? 'fa fa-minus' : 'fa fa-plus'}></i></span>
                        <span className="fx-addtrigger-tx">{showCustomDm ? 'Cancel' : 'Add another person'}</span>
                      </button>
                    )}
                    {customOpen && (
                      <div className="fx-addform" onClick={(e) => e.stopPropagation()}>
                        <label className="fx-field">
                          <i className="fa fa-user"></i>
                          <input placeholder="Full name" value={customName} onChange={(e) => setCustomName(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') addCustomPerson(); }} />
                        </label>
                        <label className="fx-field">
                          <i className="fa fa-briefcase"></i>
                          <input placeholder="Role or title (optional)" value={customRole} onChange={(e) => setCustomRole(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') addCustomPerson(); }} />
                        </label>
                        <label className={`fx-field ${needsProfileLink && showCustomDm && !customLinkedin.trim() ? 'is-required' : ''}`}>
                          <i className="fab fa-linkedin-in"></i>
                          <input placeholder={needsProfileLink ? 'LinkedIn profile URL' : 'Profile URL (optional)'} value={customLinkedin} onChange={(e) => setCustomLinkedin(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') addCustomPerson(); }} />
                        </label>
                        <button type="button" className="fx-addconfirm" disabled={!canAddCustom} onClick={addCustomPerson}>
                          <i className="fa fa-plus"></i> Add person
                        </button>
                      </div>
                    )}
                  </div>
                );
              })()}
            </>
          );

          // Reply status — the one thing the user actually cares about per step:
          // did this contact write back in the meantime?
          const hasReplied = contact.lead_reply === true || contact.lead_reply === 'true';
          const replySubtype = contact.lead_reply_subtype || null;
          const enthusiasm = contact.lead_happyness != null ? parseInt(contact.lead_happyness) : null;
          const NEG_SUBTYPES = ['hostile', 'spam_accusation', 'no_need', 'wrong_person', 'not_interested', 'unsubscribe'];
          const replyTone = replySubtype && NEG_SUBTYPES.includes(replySubtype) ? 'neg'
            : (replySubtype === 'positive' || (enthusiasm != null && enthusiasm >= 7)) ? 'pos'
            : 'neutral';
          const fmtSubtype = (s) => s ? s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : null;

          const ReplyStatus = () => (
            hasReplied ? (
              <div className={`fx-reply fx-reply--${replyTone}`}>
                <i className="fa fa-reply"></i>
                <span className="fx-reply-main">Replied</span>
                {replySubtype && <span className="fx-reply-tag">{fmtSubtype(replySubtype)}</span>}
                {enthusiasm != null && <span className="fx-reply-score">{enthusiasm}/10</span>}
              </div>
            ) : (
              <div className="fx-reply fx-reply--none">
                <i className="fa fa-inbox"></i> No reply yet
              </div>
            )
          );

          const requestDecision = (decision) => {
            if (!canAct) return;
            const pathResult = doesPathLeadToEnd(flow, displayStep, decision);
            if (pathResult.isEnd) {
              setConfirmDialog({ decision, stepIndex: displayStepIndex, contactName, isDisqualified: pathResult.isDisqualified, endType: pathResult.endType });
            } else {
              setPending(decision);
              onCompleteStep(contact.id, decision, displayStepIndex);
            }
          };

          return (
            <>
              {/* ===== Header: WHO ===== */}
              <div className="fx-head">
                {Avatar({ size: 'sm' })}
                <div className="fx-head-id">
                  <span className="fx-head-name">{contactName}</span>
                  <span className="fx-head-sub">
                    {contactEmail && <span className="fx-head-email">{contactEmail}</span>}
                    {contactEmail && <span className="fx-head-flow-dot">·</span>}
                    <span className="fx-head-flow">
                      <i className="fa fa-diagram-project"></i>
                      {flow.name || 'Flow'}
                      {startedAt && <span className="fx-head-flow-dot">·</span>}
                      {startedAt && <span>Started {formatDurationSinceStart(startedAt)}</span>}
                    </span>
                  </span>
                </div>
                {!displayReady && (
                  <span className="fx-head-status wait">
                    <i className="fa fa-clock"></i>
                    {formatTimeRemaining(displayTimeRemaining)}
                  </span>
                )}
                <button className={`fx-x fx-sidetoggle ${showSidebar ? 'active' : ''}`} onClick={() => setShowSidebar(v => !v)} aria-label="Toggle flow details" title="Flow details">
                  <i className="fa fa-stream"></i>
                </button>
                <div className="fx-menu-wrap">
                  <button className="fx-x fx-kebab" onClick={() => setMenuOpen(o => !o)} aria-label="More"><i className="fa fa-ellipsis-v"></i></button>
                  {menuOpen && (
                    <>
                      <div className="fx-menu-backdrop" onClick={() => setMenuOpen(false)}></div>
                      <div className="fx-menu">
                        <button
                          className="fx-menu-item"
                          disabled={!canAct || busy}
                          onClick={() => {
                            setMenuOpen(false);
                            if (canAct) {
                              setPending('action');
                              // Force-complete the current engine step, bypassing any
                              // delay/availability gate — advances the flow one step for testing.
                              // A "Check Reply" node branches on yes/no — without an explicit
                              // decision it fell through to a generic single-edge lookup and
                              // silently took whichever branch was stored first, regardless of
                              // whether a reply actually came in. Mirror the already-tracked
                              // lead_reply flag (same signal the "Replied"/"No reply yet"
                              // badge above uses) so forcing this step reflects reality.
                              const forcedDecision = currentStep.nodeType === 'auto_check_reply'
                                ? (hasReplied ? 'yes' : 'no')
                                : null;
                              onCompleteStep(contact.id, forcedDecision, stepIndex, null, { force: true });
                            }
                          }}
                        >
                          <i className="fa fa-forward-step"></i> Force next step (test)
                        </button>
                        <button className="fx-menu-item danger" onClick={() => { setMenuOpen(false); onStopFlow(contact.id); }}>
                          <i className="fa fa-stop-circle"></i> Stop flow
                        </button>
                      </div>
                    </>
                  )}
                </div>
                <button className="fx-x" onClick={onClose} aria-label="Close"><i className="fa fa-times"></i></button>
              </div>

              {/* ===== Body: two columns ===== */}
              <div className="fx-main">
                {/* Left: flow context + research/company info panel (collapsible) */}
                {showSidebar && (
                <aside className="fx-side">
                  <div className="fx-side-info">
                    <div className="fx-info-head">
                      <span className="fx-eyebrow fx-side-info-title">Company info</span>
                      <div className="fx-info-socials">
                        {contact.lead_website && (
                          <a className="fx-social" href={buildSocialHref(contact.lead_website, 'https://')} target="_blank" rel="noopener noreferrer" title="Website"><i className="fa fa-globe"></i></a>
                        )}
                        {contact.lead_instagram && (
                          <a className="fx-social fx-social--ig" href={buildSocialHref(contact.lead_instagram, 'https://instagram.com/')} target="_blank" rel="noopener noreferrer" title="Instagram"><i className="fab fa-instagram"></i></a>
                        )}
                        {contact.lead_facebook && (
                          <a className="fx-social fx-social--fb" href={buildSocialHref(contact.lead_facebook, 'https://facebook.com/')} target="_blank" rel="noopener noreferrer" title="Facebook"><i className="fab fa-facebook-f"></i></a>
                        )}
                        {contact.lead_linkedin && (
                          <a className="fx-social fx-social--li" href={buildSocialHref(contact.lead_linkedin, 'https://linkedin.com/in/')} target="_blank" rel="noopener noreferrer" title="LinkedIn"><i className="fab fa-linkedin-in"></i></a>
                        )}
                      </div>
                    </div>

                    {(() => {
                      const fields = [
                        { label: 'Company', value: contact.business_name, icon: 'fa fa-building' },
                        { label: 'City', value: contact.lead_city, icon: 'fa fa-location-dot' },
                        { label: 'Type', value: contact.lead_type, icon: 'fa fa-tag' },
                      ].filter(f => f.value);
                      if (!fields.length) return null;
                      return (
                        <div className="fx-info-fields">
                          {fields.map(f => (
                            <div className="fx-info-field" key={f.label} title={`${f.label}: ${f.value}`}>
                              <span className="fx-info-field-icon"><i className={f.icon}></i></span>
                              <div className="fx-info-field-text">
                                <span className="fx-info-field-label">{f.label}</span>
                                <span className="fx-info-field-value">{f.value}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      );
                    })()}

                    {cleanDescription(contact.lead_description) && (
                      <div className="fx-info-desc">
                        <span className="fx-info-desc-label">Description</span>
                        <p className="fx-info-desc-text">{cleanDescription(contact.lead_description)}</p>
                      </div>
                    )}

                    <div className="fx-side-info-body">
                      <ResearchInfoTabs contact={contact} />
                    </div>
                  </div>
                </aside>
                )}

                {/* Right: WHAT NOW — the focus */}
                <section className="fx-focus">
                  <div className="fx-focus-scroll">
                  <div className="fx-focus-inner">
                    {isCurrentCondition ? (
                      <>
                        <div className="fx-focus-icon theme-decision"><i className="fa fa-code-branch"></i></div>
                        <h2 className="fx-focus-title">{getStepLabel(displayStep, stepIndex)}</h2>
                        <div className="fx-statusrow">{InStepTimer()}{ReplyStatus()}</div>

                        <div className="fx-paths">
                          <button className="fx-paths-toggle" onClick={() => setShowPaths(v => !v)}>
                            <i className={`fa fa-chevron-${showPaths ? 'up' : 'down'}`}></i>
                            {showPaths ? 'Hide paths' : 'Where does each choice lead?'}
                          </button>
                          {showPaths && (
                            <div className="fx-outcomes">
                              <div className="fx-outcome yes">
                                <span className="fx-outcome-chip"><i className="fa fa-check"></i> Yes</span>
                                <i className="fa fa-arrow-right fx-outcome-arr"></i>
                                <span className={`fx-outcome-icon ${getStepThemeClass(yesStep)}`}><i className={yesStep ? getStepIcon(yesStep) : 'fa fa-flag-checkered'}></i></span>
                                <span className="fx-outcome-label">{yesStep ? getStepLabel(yesStep, stepIndex + 1) : 'End of flow'}</span>
                              </div>
                              <div className="fx-outcome no">
                                <span className="fx-outcome-chip"><i className="fa fa-times"></i> No</span>
                                <i className="fa fa-arrow-right fx-outcome-arr"></i>
                                <span className={`fx-outcome-icon ${getStepThemeClass(noStep)}`}><i className={noStep ? getStepIcon(noStep) : 'fa fa-flag-checkered'}></i></span>
                                <span className="fx-outcome-label">{noStep ? getStepLabel(noStep, stepIndex + 1) : 'End of flow'}</span>
                              </div>
                            </div>
                          )}
                        </div>
                      </>
                    ) : (
                      <>
                        {(!displayReady && !isAutoStep) ? (
                          <div
                            className={`fx-delay-ring ${getStepThemeClass(displayStep)}`}
                            style={{ background: `conic-gradient(var(--fx-ring, #6da7ed) ${Math.round(delayProgress * 360)}deg, rgba(104, 151, 183, 0.16) 0deg)` }}
                          >
                            <span className={`fx-delay-ring-face ${getStepThemeClass(displayStep)}`}>
                              <i className={getStepIcon(displayStep)}></i>
                            </span>
                          </div>
                        ) : (
                          <div className={`fx-focus-icon ${getStepThemeClass(displayStep)} ${displayReady ? 'is-ready' : ''}`}><i className={getStepIcon(displayStep)}></i></div>
                        )}
                        <h2 className="fx-focus-title">{getStepLabel(displayStep, stepIndex)}</h2>
                        <div className="fx-statusrow">{InStepTimer()}{ReplyStatus()}</div>

                        {engagementList.length > 0 && (
                          <div className="fx-engaged">
                            <span className="fx-engaged-label"><i className="fa fa-user-check"></i> Already reached out</span>
                            <div className="fx-engaged-list">
                              {engagementList.map(p => {
                                const profileHref = liProfileHref(p.linkedinUrl) || liPeopleSearch(`${p.name} ${contactName}`);
                                return (
                                  <a
                                    className="fx-engaged-chip"
                                    key={p.key}
                                    href={profileHref}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    title={`${p.name}${p.custom ? ' (added manually)' : ''} — ${p.linkedinUrl ? 'open profile' : 'find on LinkedIn'}`}
                                  >
                                    <span className="fx-engaged-name">{p.name}</span>
                                    <span className="fx-engaged-ch">
                                      {Object.keys(p.channels).map(ch => (
                                        <i key={ch} className={CHANNEL_META[ch]?.icon || CHANNEL_META.other.icon} title={CHANNEL_META[ch]?.label || ch}></i>
                                      ))}
                                    </span>
                                    <i className="fa fa-external-link-alt fx-engaged-open"></i>
                                  </a>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {!displayReady && !isAutoStep && (
                          <div className="fx-delay">
                            <div className="fx-delay-count">
                              <i className="fa fa-lock"></i>
                              <span className="fx-delay-count-num">{formatTimeRemaining(displayTimeRemaining)}</span>
                            </div>
                            <span className="fx-delay-sub">until this step unlocks</span>
                            <div className="fx-delay-bar"><span className="fx-delay-bar-fill" style={{ width: `${Math.round(delayProgress * 100)}%` }}></span></div>
                            {unlockAt && (
                              <span className="fx-delay-date"><i className="fa fa-calendar-day"></i> Unlocks {formatUnlockDate(unlockAt)}</span>
                            )}
                          </div>
                        )}
                        {isAutoStep && isAutoCheckReply && autoCheckDeadline ? (
                          <div className="fx-delay fx-delay--wait">
                            <div className="fx-delay-count">
                              <i className="fa fa-hourglass-half"></i>
                              <span className="fx-delay-count-num">{formatTimeRemaining(autoCheckRemaining)}</span>
                            </div>
                            <span className="fx-delay-sub">left to wait for a reply</span>
                            <div className="fx-delay-bar"><span className="fx-delay-bar-fill fx-delay-bar-fill--wait" style={{ width: `${Math.round(autoCheckProgress * 100)}%` }}></span></div>
                            <span className="fx-delay-date"><i className="fa fa-calendar-day"></i> Auto-checked by {formatUnlockDate(autoCheckDeadline)}</span>
                          </div>
                        ) : isAutoStep && (
                          <p className="fx-focus-hint">This step runs automatically — no action needed from you.</p>
                        )}

                        {isLinkedInStepType(displayStep) && (() => {
                          const dms = pickList;
                          const hasCompanyLi = !!contact.lead_linkedin;
                          const companyLi = liProfileHref(contact.lead_linkedin) || liCompanySearch(contactName);
                          const sig = contact.leadSignals || {};
                          const showFacts = (sig.industry && sig.industry !== 'unknown') || (sig.sizeProxy && sig.sizeProxy !== 'unknown');
                          return (
                            <div className="fx-lidash">
                              <div className="fx-lidash-head">
                                <span className="fx-lidash-brand"><i className="fab fa-linkedin"></i> LinkedIn</span>
                                <a className="fx-lidash-open" href={companyLi} target="_blank" rel="noopener noreferrer">
                                  {hasCompanyLi ? 'Company profile' : 'Find company'} <i className="fa fa-external-link-alt"></i>
                                </a>
                              </div>

                              {showFacts && (
                                <div className="fx-lidash-facts">
                                  {sig.industry && sig.industry !== 'unknown' && <span className="fx-fact"><i className="fa fa-briefcase"></i> {sig.industry}</span>}
                                  {sig.sizeProxy && sig.sizeProxy !== 'unknown' && <span className="fx-fact"><i className="fa fa-users"></i> {sig.sizeProxy}</span>}
                                </div>
                              )}

                              {linkedinInactive ? (
                                <div className="fx-lidash-inactive">
                                  <i className="fa fa-user-slash"></i>
                                  <span>No LinkedIn target — remaining LinkedIn steps are skipped automatically.</span>
                                </div>
                              ) : (
                                <>
                                  <div className="fx-lidash-dmhead">
                                    <span>{mustPickDm ? 'Who did you connect with?' : 'Decision makers'}</span>
                                    {dms.length > 0 && <span className="fx-lidash-count">{dms.length}</span>}
                                  </div>
                                  <div className={`fx-lidash-dms ${mustPickDm ? 'fx-dms-pick' : ''}`}>
                                    {renderDmRows()}
                                  </div>
                                  {displayReady && canAct && (
                                    <div className="fx-lidash-foot">
                                      <button
                                        type="button"
                                        className="fx-lidash-notfound"
                                        onClick={() => { if (!busy) { setPending('action'); onCompleteStep(contact.id, null, displayStepIndex, null, { markLinkedInInactive: true }); } }}
                                        disabled={busy}
                                      >
                                        No LinkedIn target?
                                      </button>
                                    </div>
                                  )}
                                </>
                              )}
                            </div>
                          );
                        })()}

                        {!isLinkedInStepType(displayStep) && mustPickDm && (
                          <div className="fx-pick">
                            <div className="fx-pick-head">
                              <span className="fx-pick-title"><i className={CHANNEL_META[stepChannel]?.icon}></i> Who did you contact via {CHANNEL_META[stepChannel]?.label || 'this channel'}?</span>
                            </div>
                            <div className="fx-lidash-dms fx-dms-pick">
                              {renderDmRows()}
                            </div>
                          </div>
                        )}

                        {isLogNotes && displayReady && (
                          <div className="fx-lognotes" onClick={(e) => e.stopPropagation()}>
                            {displayStep?.note && <p className="fx-lognotes-hint">{displayStep.note}</p>}
                            <NotesActivityFields
                              compact
                              activity={noteDraft.activity}
                              setActivity={(v) => setNoteDraft(prev => ({ ...prev, activity: v }))}
                              outcome={noteDraft.outcome}
                              setOutcome={(v) => setNoteDraft(prev => ({ ...prev, outcome: v }))}
                              sentiment={noteDraft.sentiment}
                              setSentiment={(v) => setNoteDraft(prev => ({ ...prev, sentiment: v }))}
                              text={noteDraft.text}
                              setText={(v) => setNoteDraft(prev => ({ ...prev, text: v }))}
                              excludeFromAi={noteDraft.excludeFromAi}
                              setExcludeFromAi={(v) => setNoteDraft(prev => ({ ...prev, excludeFromAi: v }))}
                              rows={3}
                            />
                          </div>
                        )}

                        {displayStep?.note && !isLogNotes && <div className="fx-focus-note">"{displayStep.note}"</div>}
                      </>
                    )}
                  </div>
                  </div>

                  {/* ===== Action bar — pinned to the bottom of the focus column ===== */}
                  {isCurrentCondition ? (
                    <div className="fx-decisionbar">
                      <button className="fx-db fx-db--no" onClick={() => requestDecision('no')} disabled={!canAct || busy}>
                        {pending === 'no' ? <i className="fa fa-spinner fa-spin"></i> : <><i className="fa fa-times"></i> No</>}
                      </button>
                      <button className="fx-db fx-db--yes" onClick={() => requestDecision('yes')} disabled={!canAct || busy}>
                        {pending === 'yes' ? <i className="fa fa-spinner fa-spin"></i> : <><i className="fa fa-check"></i> Yes</>}
                      </button>
                    </div>
                  ) : isAutoStep ? (
                    <div className="fx-actionbar fx-actionbar--auto">
                      <i className="fa fa-robot"></i> Automated — handled by the system
                    </div>
                  ) : isLogNotes ? (
                    <button
                      className="fx-actionbar fx-actionbar--cta"
                      onClick={() => {
                        if (canAct && displayReady && hasNoteContent(noteDraft)) {
                          setPending('action');
                          onCompleteStep(contact.id, null, displayStepIndex, null, { logNote: { ...noteDraft } });
                        }
                      }}
                      disabled={!canAct || busy || !displayReady || !hasNoteContent(noteDraft)}
                    >
                      {busy ? <><i className="fa fa-spinner fa-spin"></i> Saving…</>
                        : !displayReady ? <><i className="fa fa-clock"></i> Waiting…</>
                        : !hasNoteContent(noteDraft) ? <><i className="fa fa-clipboard-list"></i> Add note details</>
                        : <><i className="fa fa-clipboard-check"></i> Save note & continue</>}
                    </button>
                  ) : (!isEndStep && !isNonActionable) ? (
                    <button
                      className="fx-actionbar fx-actionbar--cta"
                      onClick={() => { if (canAct && interactionReady) { setPending('action'); onCompleteStep(contact.id, null, displayStepIndex, buildInteractions()); } }}
                      disabled={!canAct || busy || !displayReady || !interactionReady}
                    >
                      {busy ? <><i className="fa fa-spinner fa-spin"></i> Completing…</>
                        : !displayReady ? <><i className="fa fa-clock"></i> Waiting…</>
                        : mustPickDm && !hasValidPick ? <><i className="fa fa-user-plus"></i> Select who you contacted</>
                        : <><i className="fa fa-check"></i> {getActionVerb(displayStep)}{mustPickDm && selectedAll.length > 1 ? ` (${selectedAll.length})` : ''}</>}
                    </button>
                  ) : null}
                </section>
              </div>
            </>
          );
        })() : (
          <>
            <div className="fx-head">
              <Avatar size="sm" />
              <div className="fx-head-id">
                <span className="fx-head-name">{contactName}</span>
                {contactEmail && <span className="fx-head-email">{contactEmail}</span>}
              </div>
              <button className="fx-x" onClick={onClose} aria-label="Close"><i className="fa fa-times"></i></button>
            </div>

            <div className="fx-idle">
              {contact.flowEndStatus && (
                <div className={`fx-end-banner ${contact.flowEndStatus}`}>
                  <div className="fx-end-icon">
                    {FLOW_END_CONFIG[contact.flowEndStatus]?.icon
                      ? <i className={FLOW_END_CONFIG[contact.flowEndStatus].icon}></i>
                      : <span className="fx-end-fin">{FLOW_END_CONFIG[contact.flowEndStatus]?.text || 'FIN'}</span>}
                  </div>
                  <div className="fx-end-info">
                    <span className="fx-end-label">{FLOW_END_CONFIG[contact.flowEndStatus]?.label || 'Completed'}</span>
                    {contact.flowEndedAt && <span className="fx-end-date">{new Date(contact.flowEndedAt).toLocaleDateString()}</span>}
                  </div>
                  {contact.isDisqualified && (
                    <button className="fx-reactivate" onClick={() => onReactivate(contact.id)}>
                      <i className="fa fa-undo"></i> Reactivate
                    </button>
                  )}
                </div>
              )}

              {pastFlows.length > 0 && (
                <div className="fx-history">
                  <div className="fx-history-head">
                    <span className="fx-history-title"><i className="fa fa-clock-rotate-left"></i> Flow history</span>
                    <span className="fx-history-count">{pastFlows.length}</span>
                  </div>
                  <div className="fx-history-list">
                    {pastFlows.map((h, i) => {
                      const cfg = FLOW_END_CONFIG[h.reason];
                      const outcome = cfg?.label
                        || (h.reason === 'stopped' ? 'Stopped'
                          : h.reason === 'replaced' ? 'Replaced'
                          : h.reason ? h.reason.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
                          : 'Ended');
                      const dotColor = cfg?.color || (h.reason === 'stopped' ? '#f59e0b' : '#94a3b8');
                      const flowDef = warmupFlows.find(f => f.id === h.flowId);
                      const total = flowDef?.steps?.length || 0;
                      const done = h.stepHistory ? Object.keys(h.stepHistory).length : 0;
                      const reachedIdx = typeof h.lastStepIndex === 'number' ? h.lastStepIndex : null;
                      const reachedStep = reachedIdx != null && flowDef?.steps?.[reachedIdx]
                        ? getStepLabel(flowDef.steps[reachedIdx], reachedIdx) : null;
                      return (
                        <div className="fx-history-item" key={i}>
                          <span className="fx-history-dot" style={{ background: dotColor }}></span>
                          <div className="fx-history-main">
                            <span className="fx-history-name">{h.flowName || 'Flow'}</span>
                            <span className="fx-history-meta">
                              <span className="fx-history-outcome" style={{ color: dotColor }}>{outcome}</span>
                              {reachedStep && <> · reached “{reachedStep}”</>}
                              {!reachedStep && total > 0 && <> · {done}/{total} steps</>}
                            </span>
                          </div>
                          {h.endedAt && <span className="fx-history-date">{new Date(h.endedAt).toLocaleDateString()}</span>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {!contact.isDisqualified ? (
                <>
                  <div className="fx-idle-head">
                    <span className="fx-idle-eyebrow"><i className="fa fa-bolt"></i> Choose a flow</span>
                    <p className="fx-idle-title">Start a flow for <span className="fx-idle-name">{contactName}</span></p>
                  </div>
                  <div className="fx-flow-picker">
                    {warmupFlows.length === 0 ? (
                      <div className="fx-empty"><i className="fa fa-info-circle"></i><span>No flows configured yet</span></div>
                    ) : (
                      warmupFlows.map(flow => {
                        const prior = getPriorRun(flow.id);
                        const allSteps = flow.steps || [];
                        const previewSteps = allSteps.filter(s => {
                          const nt = s?.nodeType || s?.type || '';
                          return !['trigger', 'end', 'tag', 'delay'].includes(nt);
                        });
                        const shownSteps = previewSteps.slice(0, 5);
                        const extraSteps = previewSteps.length - shownSteps.length;
                        // Rough runtime = sum of the delay nodes' days.
                        const totalDays = allSteps.reduce((sum, s) => {
                          const isDelay = s?.nodeType === 'delay' || s?.type === 'delay';
                          return isDelay ? sum + (parseInt(s.delayDays) || 0) : sum;
                        }, 0);
                        return (
                          <button key={flow.id} className={`fx-flow-option ${prior ? 'has-history' : ''}`} onClick={() => handlePickFlow(flow)}>
                            <span className="fx-flow-option-icon">
                              <i className="fa fa-rocket"></i>
                              <span className="fx-flow-option-icon-ring"></span>
                            </span>
                            <div className="fx-flow-option-body">
                              <div className="fx-flow-option-top">
                                <span className="fx-flow-option-name">{flow.name}</span>
                                {prior && (
                                  <span className="fx-flow-option-warn" title="This contact was already in this flow">
                                    <i className="fa fa-history"></i> {prior.count > 1 ? `${prior.count}× before` : 'Been here'}
                                  </span>
                                )}
                              </div>

                              {shownSteps.length > 0 ? (
                                <div className="fx-flow-roadmap">
                                  {shownSteps.map((s, i) => (
                                    <React.Fragment key={i}>
                                      {i > 0 && <span className="fx-flow-connector"></span>}
                                      <span className={`fx-flow-step ${getStepThemeClass(s)}`} title={getStepLabel(s, i)}>
                                        <i className={getStepIcon(s)}></i>
                                      </span>
                                    </React.Fragment>
                                  ))}
                                  {extraSteps > 0 && (
                                    <>
                                      <span className="fx-flow-connector"></span>
                                      <span className="fx-flow-step fx-flow-step--more">+{extraSteps}</span>
                                    </>
                                  )}
                                </div>
                              ) : <div className="fx-flow-option-empty">No steps configured yet</div>}

                              <div className="fx-flow-option-meta">
                                <span className="fx-flow-meta-item"><i className="fa fa-layer-group"></i> {allSteps.length} {allSteps.length === 1 ? 'step' : 'steps'}</span>
                                {totalDays > 0 && <span className="fx-flow-meta-item"><i className="fa fa-clock"></i> ~{totalDays} {totalDays === 1 ? 'day' : 'days'}</span>}
                              </div>
                            </div>
                            <span className="fx-flow-option-go">
                              <i className="fa fa-arrow-right"></i>
                              <span className="fx-flow-option-go-label">Start</span>
                            </span>
                          </button>
                        );
                      })
                    )}
                  </div>
                </>
              ) : (
                <div className="fx-inactive">
                  <i className="fa fa-ban"></i>
                  <p>This contact is marked as inactive.</p>
                  <span>Reactivate to start a new flow.</span>
                </div>
              )}
            </div>
          </>
        )}

        {/* Re-add warning: contact was already in this flow */}
        {startConfirm && (
          <div className="fx-confirm-overlay" onClick={() => setStartConfirm(null)}>
            <div className="fx-confirm" onClick={(e) => e.stopPropagation()}>
              <div className="fx-confirm-icon warn">
                <i className="fa fa-history"></i>
              </div>
              <h3 className="fx-confirm-title">Already been in this flow</h3>
              <p className="fx-confirm-text">
                <strong>{contactName}</strong> was already in <strong>"{startConfirm.flowName}"</strong>
                {startConfirm.count > 1 ? ` ${startConfirm.count} times` : ''} and was {startConfirm.reason}
                {startConfirm.stepLabel
                  ? <> at <strong>"{startConfirm.stepLabel}"</strong>{startConfirm.stepNumber ? ` (Step ${startConfirm.stepNumber})` : ''}</>
                  : null}
                {startConfirm.endedAt ? <> on {new Date(startConfirm.endedAt).toLocaleDateString()}</> : null}.
                <br />Start over anyway?
              </p>
              <div className="fx-confirm-actions">
                <button className="fx-confirm-btn fx-confirm-cancel" onClick={() => setStartConfirm(null)}>Cancel</button>
                <button
                  className="fx-confirm-btn fx-confirm-ok"
                  onClick={() => {
                    const { flowId } = startConfirm;
                    setStartConfirm(null);
                    onStartFlow(contact.id, flowId);
                  }}
                >
                  Start again
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Safety confirmation */}
        {confirmDialog && (
          <div className="fx-confirm-overlay" onClick={() => setConfirmDialog(null)}>
            <div className="fx-confirm" onClick={(e) => e.stopPropagation()}>
              <div className={`fx-confirm-icon ${confirmDialog.isDisqualified ? 'danger' : ''}`}>
                <i className={`fa ${confirmDialog.isDisqualified ? 'fa-exclamation-triangle' : 'fa-info-circle'}`}></i>
              </div>
              <h3 className="fx-confirm-title">{confirmDialog.isDisqualified ? 'Remove from flow?' : 'End flow?'}</h3>
              <p className="fx-confirm-text">
                {confirmDialog.isDisqualified
                  ? <><strong>{confirmDialog.contactName}</strong> will be marked as disqualified and removed from this flow.</>
                  : <><strong>{confirmDialog.contactName}</strong> will reach the end of this flow after this decision.</>}
              </p>
              <div className="fx-confirm-actions">
                <button className="fx-confirm-btn fx-confirm-cancel" onClick={() => setConfirmDialog(null)}>Cancel</button>
                <button
                  className={`fx-confirm-btn ${confirmDialog.isDisqualified ? 'fx-confirm-danger' : 'fx-confirm-ok'}`}
                  disabled={!!completing}
                  onClick={() => {
                    const { decision, stepIndex } = confirmDialog;
                    setConfirmDialog(null);
                    setPending(decision);
                    onCompleteStep(contact.id, decision, stepIndex);
                  }}
                >
                  {confirmDialog.isDisqualified ? 'Yes, remove' : 'Yes, continue'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export { FLOW_END_CONFIG };
export default FlowSelectModal;
